/**
 * web/src/lib/dateIdeas.ts — THE single authoring door for a date idea
 * (card BNw401SBOLFO, IA phase 3 of 65XDhO4Qmv8q).
 *
 * A date idea enters the couple's pipeline through POST /api/dates. Before
 * this module three components posted it themselves — the Notebook capture
 * bar, the Notebook cluster's "Plan this", and the Thread nudge quick-pick —
 * and a fourth (Us → Dates) carried its own composer for the same write. Four
 * ways to write one row is how "which screen owns this?" happens.
 *
 * The write now lives here and nowhere else: `grep -rn "/api/dates'" web/src`
 * finds this line only. The surfaces keep their own verbs (capture, cluster
 * plan, quick-pick) and call this; the Us → Dates composer is gone.
 */
import { api } from '../shell/bridge';

/* ── The meta line's vocabulary (card aM7Pzbj2RmeF) ──────────────────────
   The stored values are keys — 'low'/'med'/'high', '$'..'$$$' — and were
   painted raw, so a card read "Free · In · cozy" while the label beside it
   said "In". Three surfaces build the same line (Us → Dates, the Notebook
   date chips, the Thread idea card) and each carried its own half-copy of
   this map, which is how one line ended up title case and the next sentence
   case. The maps live here; a surface with a different width budget may
   still shorten the join, but never re-word an option. */
export const DATE_COST_LABEL: Record<string, string> = {
  free: 'Free', $: 'Cheap', $$: 'Mid', $$$: 'Splurge',
};
export const DATE_ENERGY_LABEL: Record<string, string> = {
  low: 'Cozy', med: 'Easy', high: 'Big',
};
export const DATE_MODE_LABEL: Record<string, string> = {
  stay_in: 'In', go_out: 'Out', virtual: 'Virtual',
};

/** A cost band as a person reads it. Unknown values fall back to the stored
 *  band rather than to an empty string, so a band added to the union later
 *  shows something instead of vanishing from the line. */
export function dateCostLabel(band: string | null | undefined): string {
  if (!band) return '';
  return DATE_COST_LABEL[band] ?? band;
}

/** Same contract for the other two columns of the meta line. */
export function dateEnergyLabel(energy: string | null | undefined): string {
  if (!energy) return '';
  return DATE_ENERGY_LABEL[energy] ?? energy;
}

export function dateModeLabel(mode: string | null | undefined): string {
  if (!mode) return '';
  return DATE_MODE_LABEL[mode] ?? mode;
}

/** The subset of a date idea a person can author. Optional fields are omitted
 *  from the body rather than sent as undefined, so the server's own defaults
 *  (mode/cost/energy inferred from the title) still apply. */
export interface DateIdeaInput {
  title: string;
  note?: string | null;
  mode?: string;
  costBand?: string;
  energy?: string;
  /** Which surface captured it — 'nudge' for a Thread quick-pick. */
  source?: string;
}

/** Capture one idea into the couple's date pool. Throws on a failed write so
 *  callers keep their existing "the reload shows the truth" / toast handling. */
export async function createDateIdea(input: DateIdeaInput): Promise<void> {
  const body: Record<string, unknown> = { title: input.title };
  if (input.note !== undefined && input.note !== null && input.note !== '') body.note = input.note;
  if (input.mode) body.mode = input.mode;
  if (input.costBand) body.costBand = input.costBand;
  if (input.energy) body.energy = input.energy;
  if (input.source) body.source = input.source;
  await api('/api/dates', { body });
}
